import React, { useEffect, useState } from "react";
import { useAlerts } from "../hooks/useAlerts";
import { AlertData } from "../store/alertSlice";

interface AlertNotificationProps {
  alert: AlertData;
  onAcknowledge: (alertId: string) => void;
  onDismiss: (alertId: string) => void;
  onClose: () => void;
  autoClose?: boolean;
  autoCloseDelay?: number;
}

const AlertNotification: React.FC<AlertNotificationProps> = ({
  alert,
  onAcknowledge,
  onDismiss,
  onClose,
  autoClose = true,
  autoCloseDelay = 5000,
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [timeLeft, setTimeLeft] = useState(autoCloseDelay);

  useEffect(() => {
    if (!autoClose) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 100) {
          setIsVisible(false);
          setTimeout(onClose, 300); // Wait for animation
          return 0;
        }
        return prev - 100;
      });
    }, 100);

    return () => clearInterval(timer);
  }, [autoClose, autoCloseDelay, onClose]);

  const getSeverityStyles = (severity: number) => {
    switch (severity) {
      case 1:
        return "border-blue-500 bg-blue-50";
      case 2:
        return "border-green-500 bg-green-50";
      case 3:
        return "border-yellow-500 bg-yellow-50";
      case 4:
        return "border-orange-500 bg-orange-50";
      case 5:
        return "border-red-500 bg-red-50 animate-pulse";
      default:
        return "border-gray-500 bg-gray-50";
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "critical":
        return "🚨";
      case "error":
        return "❌";
      case "warning":
        return "⚠️";
      case "info":
        return "ℹ️";
      default:
        return "📢";
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case "energy":
        return "⚡";
      case "weather":
        return "🌤️";
      case "system":
        return "🔧";
      case "security":
        return "🔒";
      case "environmental":
        return "🌍";
      default:
        return "📊";
    }
  };

  const handleAcknowledge = () => {
    onAcknowledge(alert.id);
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  const handleDismiss = () => {
    onDismiss(alert.id);
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  if (!isVisible) return null;

  return (
    <div
      className={`fixed top-4 right-4 w-96 max-w-sm border-l-4 shadow-lg rounded-lg p-4 transition-all duration-300 transform ${
        isVisible ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"
      } ${getSeverityStyles(alert.severity)}`}
    >
      {/* Progress bar for auto-close */}
      {autoClose && (
        <div className="absolute top-0 left-0 h-1 bg-gray-300 rounded-t-lg w-full">
          <div
            className="h-full bg-blue-500 rounded-t-lg transition-all duration-100"
            style={{ width: `${(timeLeft / autoCloseDelay) * 100}%` }}
          />
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center space-x-2">
          <span className="text-xl">{getTypeIcon(alert.type)}</span>
          <span className="text-lg">{getCategoryIcon(alert.category)}</span>
          <span className="text-xs font-medium bg-white px-2 py-1 rounded">
            {alert.severity}/5
          </span>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 text-lg"
        >
          ×
        </button>
      </div>

      {/* Content */}
      <div className="mb-3">
        <h3 className="font-semibold text-gray-900 mb-1">{alert.title}</h3>
        <p className="text-sm text-gray-700">{alert.message}</p>

        {alert.metadata?.currentValue && (
          <div className="mt-2 text-xs text-gray-600 bg-white p-2 rounded">
            <span className="font-medium">Current:</span>{" "}
            {alert.metadata.currentValue} {alert.metadata.unit}
            {alert.metadata.threshold && (
              <span>
                {" "}
                | <span className="font-medium">Threshold:</span>{" "}
                {alert.metadata.threshold} {alert.metadata.unit}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Timestamp */}
      <div className="text-xs text-gray-500 mb-3">
        {new Date(alert.timestamp).toLocaleTimeString()}
      </div>

      {/* Actions */}
      <div className="flex space-x-2">
        {!alert.acknowledged && (
          <button
            onClick={handleAcknowledge}
            className="flex-1 px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition-colors"
          >
            Acknowledge
          </button>
        )}
        {!alert.dismissed && (
          <button
            onClick={handleDismiss}
            className="flex-1 px-3 py-1 bg-gray-500 text-white text-sm rounded hover:bg-gray-600 transition-colors"
          >
            Dismiss
          </button>
        )}
      </div>
    </div>
  );
};

export default AlertNotification;
